import { useState } from 'react';
import { createColumnHelper, type PaginationState } from '@tanstack/react-table';
import { CustomAlert } from '@aslanpay/portal-common-lib';
import { Flex, Text } from '@chakra-ui/react';

import Table from 'app/components/UI/Table/Table';
import { FailedMember } from 'app/utils/groupMembership';

const columnHelper = createColumnHelper<FailedMember>();

const columns = [
  columnHelper.accessor('name', { header: 'Employee' }),
  columnHelper.accessor('reason', { header: 'Reason' }),
];

const PAGE_SIZE = 5;

interface FailedMembersSummaryProps {
  failedMembers: FailedMember[];
  alertTitle?: string;
  sectionTitle?: string;
}

/**
 * Warning banner plus an always-visible, paginated table of the members that could not be added, for the
 * pot/group membership flows. Renders nothing when there are no failures.
 */
function FailedMembersSummary({
  failedMembers,
  alertTitle = 'Some members could not be added',
  sectionTitle = 'Members that could not be added',
}: FailedMembersSummaryProps) {
  const [pageIndex, setPageIndex] = useState(0);

  if (failedMembers.length === 0) return null;

  const pagedData = failedMembers.slice(pageIndex * PAGE_SIZE, (pageIndex + 1) * PAGE_SIZE);

  const onPaginationChange: React.Dispatch<React.SetStateAction<PaginationState>> = (updater) => {
    const next =
      typeof updater === 'function' ? updater({ pageIndex, pageSize: PAGE_SIZE }) : updater;
    setPageIndex(next.pageIndex);
  };

  return (
    <Flex direction="column" gap="12px">
      <CustomAlert variant="simple" status="warning" title={alertTitle} />
      <Text textStyle="smallSemiBold" color="brand.purple">
        {sectionTitle}{' '}
        <Text as="span" color="brand.mutedPurple" textStyle="smallRegular">
          ({failedMembers.length})
        </Text>
      </Text>
      <Table
        columns={columns}
        data={pagedData}
        pageNumber={pageIndex}
        pageSize={PAGE_SIZE}
        total={failedMembers.length}
        sorting={[]}
        onPaginationChange={onPaginationChange}
        onSortingChange={() => {}}
        showHover={false}
      />
    </Flex>
  );
}

export default FailedMembersSummary;
